import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Plus, UserPlus } from "lucide-react";

export default function StaffAssignmentDialog({ open, onClose, onSaved, event, clientId }) {
    const [staff, setStaff] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [selectedId, setSelectedId] = useState("");
    const [amount, setAmount] = useState("0");
    const [showNewFreelancer, setShowNewFreelancer] = useState(false);
    const [newForm, setNewForm] = useState({ name: "", contact_number: "", role: "Photographer", default_rate: 0 });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !event) return;
        (async () => {
            try {
                const [s, avail] = await Promise.all([
                    api.get("/staff"),
                    api.get("/staff/availability", { params: { date: event.event_date } }),
                ]);
                setStaff(s.data);
                setBookings(avail.data.bookings || []);
                setSelectedId("");
                setAmount("0");
                setShowNewFreelancer(false);
            } catch (e) {
                toast.error("Failed to load staff");
            }
        })();
    }, [open, event]);

    if (!event) return null;

    const bookedIds = new Set(bookings.map((b) => b.staff_id));

    const submit = async () => {
        if (!selectedId) return toast.error("Choose a staff member");
        setSaving(true);
        try {
            console.log(event);
            await api.post(`/clients/${clientId}/events/${event.id}/assign`, {
                staff_id: selectedId,
                payment_amount: Number(amount) || 0,
                booked_date: event.event_date,
            });
            toast.success("Staff assigned");
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const createFreelancer = async () => {
        if (!newForm.name.trim()) return toast.error("Name required");
        try {
            const { data } = await api.post("/staff", {
                ...newForm,
                default_rate: Number(newForm.default_rate) || 0,
            });
            setStaff((prev) => [...prev, data]);
            setSelectedId(data.id);
            setAmount(String(data.default_rate || 0));
            setShowNewFreelancer(false);
            toast.success("Freelancer added");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="assign-staff-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Assign staff</DialogTitle>
                    <DialogDescription>
                        {event.event_name} · {new Date(event.event_date).toLocaleDateString()}
                    </DialogDescription>
                </DialogHeader>

                {!showNewFreelancer ? (
                    <div className="space-y-4">
                        <div>
                            <Label>Choose from your team</Label>
                            <Select value={selectedId} onValueChange={(v) => {
                                setSelectedId(v);
                                const st = staff.find((s) => s.id === v);
                                if (st) setAmount(String(st.default_rate || 0));
                            }}>
                                <SelectTrigger className="mt-1" data-testid="assign-staff-select">
                                    <SelectValue placeholder="Select staff…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {staff.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-gray-500">
                                            No staff yet — add a freelancer below.
                                        </div>
                                    ) : (
                                        staff.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name} · {s.role}
                                                {bookedIds.has(s.id) ? " ⚠︎ booked" : ""}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            {selectedId && bookedIds.has(selectedId) && (
                                <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="conflict-warning">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <div>
                                        This staff member is already booked on{" "}
                                        {new Date(event.event_date).toLocaleDateString()} for another event.
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <Label>Payment for this event (Rs)</Label>
                            <Input
                                type="number"
                                min={0}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="mt-1 tabular-nums"
                                data-testid="assign-amount-input"
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => setShowNewFreelancer(true)}
                            className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                            data-testid="add-freelancer-toggle"
                        >
                            <UserPlus className="w-4 h-4" /> Add a freelancer on the fly
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3 border border-dashed border-gray-200 rounded-md p-4 bg-gray-50">
                        <div className="text-xs uppercase tracking-widest text-gray-500">New freelancer</div>
                        <div>
                            <Label>Name</Label>
                            <Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} className="mt-1" data-testid="freelancer-name-input" />
                        </div>
                        <div>
                            <Label>Contact number</Label>
                            <Input value={newForm.contact_number} onChange={(e) => setNewForm({ ...newForm, contact_number: e.target.value })} className="mt-1" data-testid="freelancer-phone-input" />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={newForm.role} onValueChange={(v) => setNewForm({ ...newForm, role: v })}>
                                <SelectTrigger className="mt-1" data-testid="freelancer-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Photographer">Photographer</SelectItem>
                                    <SelectItem value="Videographer">Videographer</SelectItem>
                                    <SelectItem value="Editor">Editor</SelectItem>
                                    <SelectItem value="Assistant">Assistant</SelectItem>
                                    <SelectItem value="Other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Default rate (Rs)</Label>
                            <Input type="number" min={0} value={newForm.default_rate} onChange={(e) => setNewForm({ ...newForm, default_rate: e.target.value })} className="mt-1 tabular-nums" data-testid="freelancer-rate-input" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowNewFreelancer(false)}>
                                Cancel
                            </Button>
                            <Button size="sm" onClick={createFreelancer} className="bg-blue-500 hover:bg-blue-600" data-testid="save-freelancer-button">
                                <Plus className="w-4 h-4 mr-1" /> Add
                            </Button>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving || !selectedId}
                        className="bg-blue-500 hover:bg-blue-600"
                        data-testid="confirm-assign-button"
                    >
                        {saving ? "Assigning…" : "Assign"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
